'use client';

import { useEffect, useState } from "react";
import NavAcessibilidade from '@/components/NavAcessibilidade/NavAcessibilidade';
import Menu from '@/components/Menu/Menu';
import Breadcrumb from '@/components/Breadcrumb/Breadcrumb';
import Footer from '@/components/Footer/Footer';
import Newsletter from '@/components/Newsletter/Newsletter';
import styles from './NossoAcervoDetalhes.module.css';
import Experimente from '@/components/Experimente/Experimente';
import PerguntaseRespostas from '@/components/PerguntaseRespotas/PerguntaseRespostas';
import ShareButtons from '@/components/ShareButtons/ShareButtons';
import classNames from 'classnames';
import useGetPublicacoesSimilares from '@/hooks/requestQueries/nossoAcervo/getPublicacoesSimilares';
import parse from 'html-react-parser';
import { sanitizeHtml } from '@/utils/sanitizeHtml';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { slugifyTitulo } from "@/utils/slugify";
import BookSchema from '@/components/BookSchema/BookSchema';

interface PublicationDetails {
    id: number;
    titulo: string;
    descricao: string;
    capa: string;
    autores: string[];
    editora: string;
    isbn: string;
    edicao: string;
    anoPublicacao: string;
    idioma: string;
    paginas: number;
    categorias: string;
    sobreAutor?: string;
    novo?: boolean;
    possuiEpub?: boolean;
    possuiPdf?: boolean;
}

interface SimilarPublication {
    publicacaoId: number;
    titulo: string;
    tituloEncurtado: string;
    autoresCompleto: string;
    autoresEncurtado: string;
    capa: string;
    novo?: boolean;
    possuiEpub?: boolean;
    possuiPdf?: boolean;
}

export type NossoAcervViewProps = {
    data: PublicationDetails;
    produtoId: number;
    id: number;
};

const NossoAcervoDetalhesView = (props: NossoAcervViewProps) => {
    const produtoId = props.produtoId || 1;

    const publicacao: PublicationDetails = props.data;

    const { data: publicacoesSimilaresData, isError: isErrorSimilares } = useGetPublicacoesSimilares(publicacao.id, produtoId);

    const [isMainImageLoaded, setIsMainImageLoaded] = useState(false);
    const [loadedImages, setLoadedImages] = useState<{ [key: number]: boolean }>({});

    const publicacoesSimilares: SimilarPublication[] = publicacoesSimilaresData?.data || [];

    const handleMainImageLoad = () => {
        setIsMainImageLoaded(true);
    };

    const handleSimilarImageLoad = (id: number) => {
        setLoadedImages((prev) => ({ ...prev, [id]: true }));
    };

    const pathname = usePathname();

    // Controle para garantir que só renderiza conteúdo dependente de window no cliente
    const [origin, setOrigin] = useState('');
    const [isClient, setIsClient] = useState(false);

    useEffect(() => {
        setIsClient(true);
        setOrigin(window.location.origin);
    }, []);

    const postUrl = isClient ? `${origin}${pathname}` : '';

    return (
        <div>
            <BookSchema publicacao={publicacao} />
            
            {/* Renderize esses componentes somente no client para evitar mismatches */}
            {isClient && <NavAcessibilidade />}
            {isClient && <Menu />}
            <div id="conteudo"></div>
            <Breadcrumb titulo="Nosso Acervo" color="blue200" />

            <section className={styles.NossoAcervoDetalhes}>
                <div className={styles.bgEsquerda} />
                <div className={styles.bgDireita} />
                <div className={styles.uiContainer}>
                    <div className={styles.dFlex}>
                        <div className={styles.imgAcervoBook}>
                            <div className={styles.acervoImagemEFlags}>
                                <div className={styles.imgAcervoBook}>
                                    <img
                                        tabIndex={0}
                                        src={publicacao?.capa}
                                        alt={`Capa do livro ${publicacao?.titulo}`}
                                        loading="lazy"
                                        onError={(e) => (e.currentTarget.src = '/images/book_default.jpg')}
                                        onLoad={handleMainImageLoad}
                                        width={222}
                                        height={330}
                                        className={styles.imgBox}
                                    />
                                </div>
                                {isMainImageLoaded && (
                                    <div className={styles.containerImgFlags}>
                                        {publicacao?.novo && <div className={classNames(styles.itemFlag, styles.novo)}>Novo</div>}
                                        {publicacao?.possuiEpub && <div className={classNames(styles.itemFlag, styles.epub)}>Epub</div>}
                                        {publicacao?.possuiPdf && <div className={classNames(styles.itemFlag, styles.pdf)}>PDF</div>}
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className={styles.contentAcervo}>
                            <div className={styles.infoLivroAcervo}>
                                <h2 tabIndex={0} className={styles.nome}>{publicacao?.titulo}</h2>
                                {publicacao?.autores?.length > 0 && (
                                    <p tabIndex={0} className={styles.autor}>
                                        {publicacao.autores.map((autor, index) => (
                                            <span key={index}>
                                                <Link 
                                                    href={`/NossoAcervo?search=${encodeURIComponent(autor)}&produtoId=${produtoId}`}
                                                    className={styles.autorLink}
                                                >
                                                    {autor}
                                                </Link>
                                                {index < publicacao.autores.length - 1 && '; '}
                                            </span>
                                        ))}
                                    </p>
                                )}
                                <div className={styles.sobreLivro}>
                                    <p tabIndex={0}><span>Editora:</span> {publicacao?.editora}</p>
                                    <p tabIndex={0}><span>ISBN:</span> {publicacao?.isbn}</p>
                                    <p tabIndex={0}><span>Edição/Ano:</span> {publicacao?.edicao}ª ({publicacao?.anoPublicacao})</p>
                                    <p tabIndex={0}><span>Idioma:</span> {publicacao?.idioma}</p>
                                    <p tabIndex={0}><span>Páginas:</span> {publicacao?.paginas}</p>
                                    <p tabIndex={0}>
                                        <span>Categoria(s):</span> {publicacao?.categorias ? publicacao.categorias : 'Sem categorias'}
                                    </p>
                                </div>
                                <hr />
                            </div>

                            <div className={styles.descricaoAcervo}>
                                <p tabIndex={0} className={styles.bold}>Descrição</p>
                                 <p tabIndex={0}>{parse(publicacao?.descricao)}</p>
                                {publicacao?.sobreAutor?.trim() && (
                                    <>
                                        <hr />
                                        <p tabIndex={0} className={classNames(styles.bold, styles.sobreAutor)}>Sobre o Autor</p>
                                        <p tabIndex={0} dangerouslySetInnerHTML={{ __html: sanitizeHtml(publicacao.sobreAutor) }} />
                                    </>
                                )} 
                                
                                 {isClient && postUrl && (
                                    <ShareButtons postUrl={postUrl} postTitle={publicacao?.titulo} />
                                )} 
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <section className={styles.livrosSimilares}>
                {!isErrorSimilares && publicacoesSimilares.length > 0 && (
                    <div className={styles.uiContainer}>
                        <h2 className={styles.title} tabIndex={0}>Livros Similares</h2>
                        <div className={styles.containerLivrosSimilares}>
                            {publicacoesSimilares.map((livro) => (
                                <div key={livro.publicacaoId} className={styles.itemLivroSimilar}>
                                    <Link
                                        href={{
                                            pathname: `/NossoAcervo/Publicacao/${slugifyTitulo(livro.titulo)}-${livro.publicacaoId}`,
                                        }}
                                        className={styles.itemAcervo}
                                        aria-label={`Ver detalhes do livro ${livro.titulo} por ${livro.autoresCompleto}`}
                                    >
                                        <article>
                                            <div className={styles.acervoImagemEFlags}>
                                                <div className={styles.imgAcervo}>
                                                    <img
                                                        tabIndex={0}
                                                        src={livro.capa}
                                                        alt={`Capa do livro ${livro.titulo}`}
                                                        loading="lazy"
                                                        onError={(e) => (e.currentTarget.src = '/images/book_default.jpg')}
                                                        onLoad={() => handleSimilarImageLoad(livro.publicacaoId)}
                                                        width={222}
                                                        height={330}
                                                    />
                                                </div>
                                                {loadedImages[livro.publicacaoId] && (
                                                    <div className={styles.containerImgFlags}>
                                                        {livro.possuiPdf && (
                                                            <div className={classNames(styles.itemFlag, styles.pdf)}>PDF</div>
                                                        )}
                                                        {livro.possuiEpub && (
                                                            <div className={classNames(styles.itemFlag, styles.epub)}>Epub</div>
                                                        )}
                                                        {livro.novo && (
                                                            <div className={classNames(styles.itemFlag, styles.novo)}>Novo</div>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                            <div className={styles.contentAcervo}>
                                                <h3 tabIndex={0} className={styles.tituloLivro} title={livro.titulo}>{livro.tituloEncurtado}</h3>
                                                <p tabIndex={0} className={styles.autorLivro} title={livro.autoresCompleto}>{livro.autoresEncurtado}</p>
                                            </div>
                                        </article>
                                    </Link>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </section>

            <Experimente />
            <PerguntaseRespostas />
            <Newsletter />
            <Footer />
        </div>
    );
};

export default NossoAcervoDetalhesView;
